import { defineStore } from 'pinia'
import type {
  ClientPortalCaseSchema,
  ClientPortalOrganisationSchema,
  ClientPortalQuestionnaireSubmissionSchema,
  ClientPortalQuestionnaireSubmissionUpdatePayload,
  CreateQuestionnaireSubmission,
  Item,
  Page,
  Request,
  RequestThirdPartyDocumentLink
} from '@/types'
import type { AxiosResponse } from 'axios'
import ClientActionService from '../services/clientaction.service'
import { generateId } from '@/lib/utils'

interface ClientActionState {
  clientAction: ClientPortalCaseSchema | null
  clientOrganisation: ClientPortalOrganisationSchema | null // #TODO: Proper typing here
  clientAccessToken: string | null
  currentSubmissionId: string
}

const defaultClientAction: ClientPortalCaseSchema = {
  data: {
    client: {
      title: '',
      city: '',
      date_of_birth: '',
      email: '',
      first_name: '',
      last_name: '',
      phone: '',
      street: '',
      street_number: '',
      zip: ''
    },
    opponent: {
      title: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      street: '',
      street_number: '',
      zip: '',
      city: '',
      date_of_birth: ''
    },
    law: {
      field: '',
      topic: ''
    }
  },
  id: 0,
  inquiry: '',
  org_name: undefined,
  org_id: 0,
  requests: [],
  submissions: []
}

export const useClientActionStore = defineStore('clientAction', {
  state: (): ClientActionState => ({
    clientAction: null,
    clientAccessToken: null,
    clientOrganisation: null,
    currentSubmissionId: ''
  }),
  actions: {
    setAccessToken(token: string) {
      this.clientAccessToken = token
    },

    setCurrentSubmissionId(id: string) {
      this.currentSubmissionId = id
    },

    async fetchClientActions(): Promise<number> {
      try {
        const response: AxiosResponse<ClientPortalCaseSchema[]> =
          await ClientActionService.retrieveClientActions(this.clientAccessToken!)
        this.clientAction = response.data.length > 0 ? response.data[0] : null
        return response.status
      } catch (error: any) {
        return error.response ? error.response.status : 500
      }
    },

    async fetchOrganisation(): Promise<number> {
      try {
        const response: AxiosResponse<any> = await ClientActionService.retrieveOrganisation(
          this.clientAccessToken!
        )
        this.clientOrganisation = response.data
        return response.status
      } catch (error: any) {
        return error.response ? error.response.status : 500
      }
    },

    async fetchClientActionsPreview(questionnaire_uuid: String): Promise<number> {
      try {
        const response = await ClientActionService.retrieveClientActionsPreview(questionnaire_uuid)
        this.clientAction = defaultClientAction

        const previewSubmissions: ClientPortalQuestionnaireSubmissionSchema = {
          id: response.data.id,
          createdAt: '',
          shortId: '',
          token: '',
          title: response.data.title,
          lawField: '',
          lawTopic: '',
          pdfUrl: '',
          questionCount: 0,
          requiredQuestionCount: 0,
          optionalQuestionCount: 0,
          answeredQuestionCount: 0,
          answeredRequiredQuestionCount: 0,
          answeredOptionalQuestionCount: 0,
          answeredQuestionsPercent: 0,
          answeredRequiredQuestionsPercent: 0,
          answeredOptionalQuestionsPercent: 0,
          dueAt: '',
          state: '',
          thirdPartyAccessToken: '',
          themeColor: response.data.theme_color,
          schema_: response.data.schema
        }
        this.clientAction.submissions = [previewSubmissions]
        return response.status
      } catch (error: any) {
        console.error('An error occurred:', error)
        return error.response ? error.response.status : 500
      }
    },

    updateSubmissionSchema(submissionId: string, updatedSchema: Array<any>) {
      if (!this.clientAction || !this.clientAction.submissions) {
        console.error('No client action or submissions available')
        return
      }

      const submissionIndex = this.clientAction.submissions.findIndex(
        (sub) => sub.shortId === submissionId
      )
      if (submissionIndex === -1) {
        console.error('Submission not found')
      }

      this.clientAction.submissions[submissionIndex].schema_ = updatedSchema
    },

    async updateSubmissionItem(
      fieldUuid: string,
      newValue: string,
      close: boolean = false
    ): Promise<number> {
      let updatedSchema: Page[] | undefined
      const submission = this.clientAction?.submissions.find(
        (sub) => sub.shortId === this.currentSubmissionId
      )

      if (submission && submission.schema_) {
        updatedSchema = submission.schema_.map((page: Page) => ({
          ...page,
          items: page.items.map((item: Item) => {
            if (item.uuid === fieldUuid) {
              return { ...item, value: newValue }
            }
            return item
          })
        }))
      }

      // Early return if we don't have an updated schema to avoid unnecessary API call
      if (!updatedSchema) {
        return 500
      }

      this.updateSubmissionSchema(this.currentSubmissionId, updatedSchema)

      // Construct the request body
      const requestBody: ClientPortalQuestionnaireSubmissionUpdatePayload = {
        close: close,
        form_schema: updatedSchema
      }

      try {
        const response: AxiosResponse<any> = await ClientActionService.updateSubmission(
          this.currentSubmissionId,
          this.clientAccessToken!,
          requestBody
        )
        return response.status
      } catch (error: any) {
        return error.response ? error.response.status : 500
      }
    },

    async getQuestionnaireExternalRedirectToken(
      org_token: string,
      questionnaire_token: string
    ): Promise<Record<any, any>> {
      const reqBody: CreateQuestionnaireSubmission = {
        org_questionnaire_creation_token: org_token,
        questionnaire_creation_token: questionnaire_token
      }

      try {
        const response: AxiosResponse<any> =
          await ClientActionService.createQuestionnaireSubmission(reqBody)
        if (response.status === 200) {
          return {
            token: response.data.thirdPartyAccessToken,
            short_id: response.data.shortId
          }
        } else {
          return {}
        }
      } catch (error: any) {
        return error.response ? error.response.status : 500
      }
    },

    async getPdfExternalRedirectToken(
      creation_token: string,
      case_uuid: string
    ): Promise<Record<any, any>> {
      const reqBody: RequestThirdPartyDocumentLink = {
        creation_token,
        case_uuid
      }

      try {
        const response: AxiosResponse<any> =
          await ClientActionService.createRequestableDocument(reqBody)
        if (response.status === 200) {
          return {
            form_token: response.data.form_token
          }
        } else {
          return {}
        }
      } catch (error: any) {
        return error.response ? error.response.status : 500
      }
    },

    async fetchQuestionnaireSubmissionsForExternalView(): Promise<number> {
      try {
        const response: AxiosResponse<ClientPortalQuestionnaireSubmissionSchema> =
          await ClientActionService.retrieveQuestionnaireSubmission(
            this.clientAccessToken!,
            this.currentSubmissionId
          )
        this.clientAction = defaultClientAction

        if (this.clientAction) {
          this.clientAction.submissions = [response.data]
        }
        return response.status
      } catch (error: any) {
        // Log the error and return a fallback error status
        console.error('An error occurred:', error)
        return error.response ? error.response.status : 500
      }
    }
  },
  getters: {
    getToken: (state) => state.clientAccessToken,
    getClientAction: (state) => state.clientAction,
    getClientSubmissions: (state) =>
      state.clientAction
        ? state.clientAction.submissions
        : ([] as ClientPortalQuestionnaireSubmissionSchema[]),
    getClientRequests: (state) =>
      state.clientAction ? state.clientAction.requests : ([] as Request[]),
    getFirstName: (state) => (state.clientAction ? state.clientAction.data.client.first_name : ''),
    getLastName: (state) => (state.clientAction ? state.clientAction.data.client.last_name : ''),
    getLawField: (state) => (state.clientAction ? state.clientAction.data.law.field : ''),
    getLawTopic: (state) => (state.clientAction ? state.clientAction.data.law.topic : ''),
    getCurrentSubmissionId: (state) => state.currentSubmissionId,
    getOrganisation: (state) => state.clientOrganisation
  }
})
